<script setup lang="ts">
import PlaceLeaveFeedback from "./PlaceLeaveFeedback.vue";
import { computed, ComputedRef, onBeforeMount, ref } from "vue";
import {
  IPlaceInfo,
  usePlaceReviewsStore,
  usePlaceStore,
} from "@/modules/place-module";
import PlaceReviewItem from "./PlaceReviewItem.vue";
import { UiChip, UiSkeleton } from "@packages/component-library";
import { IconStar100Regular } from "@packages/icon-library";
import AppPagination from "@/components/AppPagination.vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["ratePlace"]);
const { t } = useI18n();
const reviewsStore = usePlaceReviewsStore();
const placeStore = usePlaceStore();
const place: ComputedRef<null | IPlaceInfo> = computed(() => placeStore.place);
const reviewsLoadingStatus = computed(() => reviewsStore.reviewsLoadingStatus);
const reviewsMeta = computed(() => reviewsStore.reviewsMeta);
const reviews = computed(() => reviewsStore.reviews);
const reviewsFilters = computed(() => reviewsStore.reviewsMeta?.rating_filters);
const reviewsPagination = computed(() => reviewsStore.reviewsPagination);
const pageLoading = ref(false);
const params = ref({
  page: 1,
  rating: [],
});

const removeFilter = (filter: string) => {
  params.value.rating = params.value.rating.filter((item) => item !== filter);
  params.value.page = 1;
  loadPlaceReviews();
};

const setFilter = (filter: string) => {
  if (!params.value.rating.includes(filter)) {
    params.value.rating.push(filter);
    params.value.page = 1;
    loadPlaceReviews();
  }
};

const clearFilter = () => {
  params.value.rating = [];
  params.value.page = 1;
  loadPlaceReviews();
};

const loadPlaceReviews = () => {
  pageLoading.value = true;
  reviewsStore
    .getPlaceReviews(place.value?.place?.id, params.value)
    .finally(() => {
      pageLoading.value = false;
    });
};

const loadNextPage = () => {
  params.value.page++;
  loadPlaceReviews();
};

onBeforeMount(() => {
  loadPlaceReviews();
});
</script>

<template>
  <section class="place-reviews">
    <div class="place-reviews__info">
      <PlaceLeaveFeedback
        v-if="reviewsMeta"
        :reviews-meta="reviewsMeta"
        @ratePlace="emit('ratePlace', $event)"
      />

      <UiSkeleton v-else />
    </div>

    <div v-if="reviewsFilters?.length > 1" class="place-reviews__filters">
      <template v-if="reviewsFilters">
        <UiChip
          :label="t('system.all')"
          size="m"
          :active="!params.rating.length"
          @select="clearFilter"
        />
        <UiChip
          v-for="(rating, index) in reviewsFilters"
          :key="index"
          :label="rating"
          size="m"
          :active="params.rating.includes(rating)"
          :is-closed="params.rating.includes(rating)"
          @select="setFilter(rating)"
          @remove="removeFilter(rating)"
        >
          <IconStar100Regular />
        </UiChip>
      </template>

      <template v-else>
        <UiSkeleton v-for="item in 3" :key="item" />
      </template>
    </div>

    <div class="place-reviews__list">
      <template v-if="reviewsLoadingStatus === 'loaded'">
        <PlaceReviewItem
          v-for="(review, index) in reviews"
          :key="index"
          :review="review"
        />
      </template>

      <template v-else>
        <UiSkeleton v-for="item in 4" :key="item" />
      </template>
    </div>

    <AppPagination
      v-if="
        reviewsPagination &&
        reviewsPagination.has_more_pages &&
        reviewsLoadingStatus === 'loaded'
      "
      :page-loading="pageLoading"
      @nextPage="loadNextPage"
    />
  </section>
</template>

<style scoped lang="scss">
.place-reviews {
  padding: calc($base-space * 6) 0;

  @media (max-width: 767px) {
    padding: calc($base-space * 5) 0;
  }

  &__info {
    padding: 0 calc($base-space * 8) calc($base-space * 6);
    border-bottom: 1px solid var(--color-semantic-border-normal-soft);

    @media (max-width: 1023px) {
      padding: 0 calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: 0 calc($base-space * 4) calc($base-space * 5);
    }

    > .ui-skeleton {
      height: 152px;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: calc($base-space * 2);
    overflow: auto;
    @include hideScroll();
    margin: calc($base-space * 6) 0 0 calc($base-space * 8);
    padding: 0 calc($base-space * 8) 0 0;

    @media (max-width: 1023px) {
      margin: calc($base-space * 6) 0 0 calc($base-space * 6);
      padding: 0 calc($base-space * 6) 0 0;
    }

    @media (max-width: 767px) {
      margin: calc($base-space * 5) 0 0 calc($base-space * 4);
      padding: 0 calc($base-space * 4) 0 0;
    }

    > .ui-skeleton {
      height: 40px;
    }

    > .ui-chip {
      flex-shrink: 0;
    }
  }

  &__list {
    padding: 0 calc($base-space * 8);
    margin: calc($base-space * 6) 0 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: calc($base-space * 5);

    @media (max-width: 1023px) {
      padding: 0 calc($base-space * 6) 0;
      gap: calc($base-space * 6);
    }

    @media (max-width: 767px) {
      margin: calc($base-space * 5) 0 0;
      padding: 0 calc($base-space * 4) 0;
      gap: calc($base-space * 5);
    }

    > .ui-skeleton {
      height: 172px;
    }
  }

  > .app-pagination {
    margin: calc($base-space * 4) 0 0;
  }
}
</style>
